import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, type CurrentUser } from '../api';
import { Button } from './ui';
import { IconArrowRight, IconCheckCircle, IconClipboard, IconServer, IconShield, IconUsers } from './icons';
import { NhcLogo } from './NhcLogo';
import { canWorkItQueue, isAdmin, isApprovedViewer, isApprover } from '../roles';

type Slide = { icon: React.ReactNode; title: string; body: string };

/**
 * What this system is, shown once.
 *
 * Tailored by role, because a requester and an administrator have almost
 * nothing in common here: telling a requester about the IT queue is noise, and
 * not telling an administrator about it is a gap. Everyone gets the first and
 * last slide; the middle is theirs.
 */
function slidesFor(user: CurrentUser): Slide[] {
  const opening: Slide = {
    icon: <IconClipboard />,
    title: 'This replaces the paper form',
    body:
      'The New User IT Enrolment Form, as a website. Same questions, same three signatures, but it saves as you go, reminds people when something is waiting on them, and the finished record cannot be edited afterwards.',
  };

  const closing: Slide = {
    icon: <IconCheckCircle />,
    title: 'Everything is signed and kept',
    body:
      'Every signature is composited into a record that cannot be changed afterwards, and every request stays under All requests for as long as you need it. If you get stuck, the person who set this up is one email away.',
  };

  if (user.role === 'PENDING') {
    return [
      opening,
      {
        icon: <IconUsers />,
        title: 'You are waiting for access',
        body:
          'Signing in worked. What is missing is a role, which an administrator assigns. Until then there is deliberately nothing here to see. They have been told you are waiting.',
      },
    ];
  }

  if (isApprovedViewer(user.role)) {
    return [
      opening,
      {
        icon: <IconCheckCircle />,
        title: 'You can read approved requests',
        body:
          'Every request that has been approved, whether still with IT or completed, is listed for you, each with its signed record. That is the whole of this account: it cannot raise, approve, chase or change anything.',
      },
    ];
  }

  const middle: Slide[] = [
    {
      icon: <IconClipboard />,
      title: 'Raising a request',
      body:
        'New enrolment walks you through eight steps and saves after every keystroke, so you can gather details over days. Offboarding is the same in reverse, and can pull in what somebody was actually given.',
    },
  ];

  if (isApprover(user.role)) {
    middle.push({
      icon: <IconCheckCircle />,
      title: 'Approving',
      body:
        'The approval queue is shared: any approver can take any request, so nothing waits on one person being at their desk. You can amend before signing, or send it back with a note saying what needs fixing.',
    });
  }

  if (isAdmin(user.role)) {
    middle.push({
      icon: <IconServer />,
      title: 'The IT side',
      body:
        'Approved work lands in the IT queue. Pick one up, tick items off as you do them, record the technician, and sign off. Settings is where you edit what the form itself offers.',
    });
  } else if (canWorkItQueue(user.role)) {
    middle.push({
      icon: <IconServer />,
      title: 'The IT queue is yours',
      body:
        'Approved work lands in the IT queue. Pick one up, tick items off as you do them, record the technician, and sign off.',
    });
  }

  // Two tiers, two different promises, so neither gets a slide that is not
  // true of them: a Super Admin's account *can* be changed, by the tier above.
  if (user.role === 'SYSTEM_ADMIN') {
    middle.push({
      icon: <IconShield />,
      title: 'You are a system administrator',
      body:
        'Everything an administrator can do, plus authority over the administrators themselves. Your own account cannot be changed from inside the app: it comes from the server configuration, so nobody can lock you out or promote themselves past you.',
    });
  } else if (user.role === 'SUPER_ADMIN') {
    middle.push({
      icon: <IconShield />,
      title: 'You are a Super Admin',
      body:
        'Everything an administrator can do, plus authority over the administrators themselves: you can grant and withdraw the administrator role, and Super Admin itself. Ordinary administrators cannot change your account, and neither can another Super Admin.',
    });
  }

  return [opening, ...middle, closing];
}

export function Walkthrough({ user, onDone }: { user: CurrentUser; onDone: () => void }) {
  const [index, setIndex] = useState(0);
  const slides = slidesFor(user);
  const last = index === slides.length - 1;

  const finish = useMutation({
    mutationFn: () => api.walkthroughSeen(),
    // Marking it seen is a convenience, not a gate. If the write fails the
    // person should still get into the app; they will just see this again.
    onSettled: onDone,
  });

  // No backdrop click and no Escape, so a stray tap does not throw it away
  // unread. The ways out are Skip, on every screen, or through to the end;
  // both mark it seen, and anyone can run it again from the account menu.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight' && !last) setIndex((i) => i + 1);
      if (event.key === 'ArrowLeft' && index > 0) setIndex((i) => i - 1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [index, last]);

  const slide = slides[index]!;

  return (
    <div
      className="animate-fade fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Welcome"
    >
      <div className="animate-pop w-full max-w-lg overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center gap-3 border-b border-slate-100 px-6 py-4 dark:border-slate-800">
          <NhcLogo className="h-7 w-auto" />
          <span className="text-sm font-semibold tracking-tight text-slate-900 dark:text-white">
            NHC IT Enrolment
          </span>
          <span className="ml-auto text-xs text-slate-500 dark:text-slate-400">
            {index + 1} of {slides.length}
          </span>
        </div>

        {/* Keyed so each slide animates in rather than the text swapping. */}
        <div key={index} className="animate-rise px-6 py-8 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-100 text-brand-700 dark:bg-brand-900/60 dark:text-brand-200">
            {slide.icon}
          </span>
          <h2 className="mt-5 text-xl font-semibold tracking-tight text-slate-900 dark:text-white">
            {slide.title}
          </h2>
          <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-slate-600 dark:text-slate-400">
            {slide.body}
          </p>
        </div>

        <div className="flex items-center gap-3 border-t border-slate-100 px-6 py-4 dark:border-slate-800">
          <div className="flex gap-1.5" aria-hidden="true">
            {slides.map((_, dot) => (
              <span
                key={dot}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  dot === index ? 'w-5 bg-brand-600' : 'w-1.5 bg-slate-300 dark:bg-slate-700'
                }`}
              />
            ))}
          </div>

          <div className="ml-auto flex gap-2">
            {!last && (
              <Button variant="ghost" size="sm" disabled={finish.isPending} onClick={() => finish.mutate()}>
                Skip
              </Button>
            )}
            {index > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setIndex(index - 1)}>
                Back
              </Button>
            )}
            {last ? (
              <Button size="sm" disabled={finish.isPending} onClick={() => finish.mutate()}>
                {finish.isPending ? 'Finishing…' : 'Get started'}
              </Button>
            ) : (
              <Button size="sm" onClick={() => setIndex(index + 1)}>
                Next
                <IconArrowRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
